import { type ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useSession } from './SessionProvider'
import { hasAnyRole, type Role } from './roles'
import { FullPageSpinner } from '@/components/FullPageSpinner'

/**
 * Route-level guards (UI-RECOMMENDATIONS §5.2). Guarding happens at three
 * levels: route (here), nav rendering, and row-level actions. The backend
 * always re-checks — UI gating is UX, not security.
 */

export function RequireAuth() {
  const { user, isLoading } = useSession()
  const location = useLocation()

  if (isLoading) return <FullPageSpinner />
  if (!user) {
    const returnTo = location.pathname + location.search
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }
  return <Outlet />
}

export function RequireRole({ roles }: { roles: Role[] }) {
  const { user, isLoading } = useSession()

  if (isLoading) return <FullPageSpinner />
  if (!user || !hasAnyRole(user, roles)) {
    return <Navigate to="/forbidden" replace />
  }
  return <Outlet />
}

/**
 * Section/action-level gate: renders children only when the signed-in user
 * holds one of the given roles. Use `fallback` to explain instead of hiding
 * where that helps ("You don't have permission to do this").
 */
export function RequiresRole({
  roles,
  children,
  fallback = null,
}: {
  roles: Role[]
  children: ReactNode
  fallback?: ReactNode
}) {
  const { user } = useSession()
  if (!hasAnyRole(user, roles)) return <>{fallback}</>
  return <>{children}</>
}
